import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import imageHelper from '../../helpers/image';
import cameraHelper from '../../helpers/camera';

import '../../assets/styles/scanner.css';

export default class CameraCapture extends Component {
	constructor(props) {
		super(props);
		this.videoRef = React.createRef();
		this.stream = null;
		this.state = {
			capturedImage: null,
			capturedFile: null,
			cameraError: null,
			isCapturing: false
		};
	}

	componentDidUpdate(prevProps) {
		if (!this.props.visible && prevProps.visible) {
			this.resetCapture();
		}
	}

	componentWillUnmount() {
		this.stopCamera();
		this.revokePreview();
	}

	resetCapture() {
		this.stopCamera();
		this.revokePreview();
		this.setState({
			capturedImage: null,
			capturedFile: null,
			cameraError: null,
			isCapturing: false
		});
	}

	revokePreview() {
		if (this.state.capturedImage) {
			URL.revokeObjectURL(this.state.capturedImage);
		}
	}

	startCamera() {
		this.setState({ cameraError: null, capturedImage: null, capturedFile: null });

		cameraHelper.requestCameraStream()
			.then((stream) => {
				this.stream = stream;
				cameraHelper.attachStreamToVideo(this.videoRef.current, stream);
			})
			.catch((error) => {
				this.setState({ cameraError: error.message || 'Could not access camera. Please allow camera permission and try again.' });
			});
	}

	onModalEntered() {
		if (this.props.visible) {
			this.startCamera();
		}
	}

	onModalExited() {
		this.resetCapture();
	}

	stopCamera() {
		if (this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
			this.stream = null;
		}

		if (this.videoRef.current) {
			this.videoRef.current.srcObject = null;
		}
	}

	handleClose() {
		this.resetCapture();
		this.props.onClose();
	}

	takePhoto() {
		const video = this.videoRef.current;
		if (!video) return;

		this.setState({ isCapturing: true });

		imageHelper.capturePhotoFromVideo(video)
			.then((file) => {
				this.stopCamera();
				const previewUrl = URL.createObjectURL(file);
				this.setState({
					capturedImage: previewUrl,
					capturedFile: file,
					isCapturing: false
				});
			})
			.catch((error) => {
				this.setState({ isCapturing: false });
				this.props.onError(error.message || 'Could not take photo. Please try again.');
			});
	}

	retakePhoto() {
		this.revokePreview();
		this.setState({ capturedImage: null, capturedFile: null }, () => {
			this.startCamera();
		});
	}

	savePhoto() {
		const { capturedFile } = this.state;
		if (!capturedFile) return;
		this.props.onSave(capturedFile);
	}

	renderCameraView() {
		const { cameraError, isCapturing } = this.state;

		if (cameraError) {
			return (
				<div className="camera-message">
					<p>{cameraError}</p>
				</div>
			);
		}

		return (
			<div className="camera-preview">
				<video ref={this.videoRef} autoPlay playsInline muted className="camera-video" />
				<div className="camera-frame" />
				<p className="camera-hint">Position your face in the frame</p>
			</div>
		);
	}

	renderPreview() {
		return (
			<div className="camera-preview">
				<img src={this.state.capturedImage} alt="Captured profile" className="camera-photo-preview" />
				<p className="camera-hint">Review your photo before saving</p>
			</div>
		);
	}

	render() {
		const { visible, isUploading } = this.props;
		const { capturedImage, isCapturing } = this.state;
		const hasPreview = !!capturedImage;

		return (
			<Modal
				show={visible}
				onHide={this.handleClose.bind(this)}
				onEntered={this.onModalEntered.bind(this)}
				onExited={this.onModalExited.bind(this)}
				dialogClassName="scanner camera-capture"
			>
				<Modal.Body>
					<div className="camera-header">
						<button type="button" className="camera-back-button" onClick={this.handleClose.bind(this)} aria-label="Close">
							<FontAwesomeIcon icon={faArrowLeft} />
						</button>
						<h4 className="camera-title">{hasPreview ? 'Review Photo' : 'Take Photo'}</h4>
					</div>

					{hasPreview ? this.renderPreview() : this.renderCameraView()}

					<div className="camera-actions">
						{hasPreview ? (
							<div>
								<button
									type="button"
									className="btn btn-default btn-block camera-action-button"
									onClick={this.retakePhoto.bind(this)}
									disabled={isUploading}
								>
									Retake
								</button>
								<button
									type="button"
									className="btn btn-primary btn-block camera-action-button"
									onClick={this.savePhoto.bind(this)}
									disabled={isUploading}
								>
									{isUploading ? 'Saving...' : 'Save Photo'}
								</button>
							</div>
						) : (
							<button
								type="button"
								className="btn btn-primary btn-block camera-action-button"
								onClick={this.takePhoto.bind(this)}
								disabled={!!this.state.cameraError || isCapturing}
							>
								{isCapturing ? 'Capturing...' : 'Take Photo'}
							</button>
						)}
					</div>
				</Modal.Body>
			</Modal>
		);
	}
}
